import { useEffect, useState } from 'react';
import { useSystem } from '../context/SystemContext';

/**
 * Matches for a search in the OTHER enabled systems (issue 1452).
 *
 * A new visitor starts on Pathfinder; a spell that only D&D 3.5e has came back
 * as a bare "No spells found", with nothing saying the other system has it. A
 * search that finds nothing here asks every other enabled system once, so the
 * page can say where the match is and offer to open it or switch.
 *
 * The systems are the ones /api/systems gave at boot (SystemContext), in that
 * order, never a named pair. Nothing is asked unless `enabled` (the caller's
 * own search came back empty) and the query is 2+ characters; at most one
 * request per other system, all aborted when the query changes. A failed
 * request counts as no match, so a failure leaves the plain empty state.
 *
 * `ask(systemSlug, query, signal)` returns a promise of `[{ slug, name, ... }]`
 * for that system. Returns `[{ system: { slug, name }, items }]`, systems with
 * no match left out, and only for the query in hand.
 */
export default function useOtherSystemMatches(query, enabled, ask) {
  const { system, systems } = useSystem();
  const q = String(query || '').trim();
  const others = (systems || []).filter((s) => s && s.slug && s.slug !== system);
  const othersKey = others.map((s) => s.slug).join(',');
  const [answer, setAnswer] = useState({ query: null, key: '', groups: [] });

  useEffect(() => {
    if (!enabled || q.length < 2 || !others.length) return undefined;
    const controller = new AbortController();
    Promise.all(others.map((s) => Promise.resolve()
      .then(() => ask(s.slug, q, controller.signal))
      .then((items) => ({ system: s, items: Array.isArray(items) ? items : [] }), () => ({ system: s, items: [] }))))
      .then((groups) => {
        if (controller.signal.aborted) return;
        setAnswer({ query: q, key: othersKey, groups: groups.filter((g) => g.items.length) });
      });
    return () => controller.abort();
  }, [enabled, q, othersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return enabled && answer.query === q && answer.key === othersKey ? answer.groups : [];
}

/** The address of an entry in another system: a full load, since ?system= is read at load (config/system.js). */
export function otherSystemHref(segment, slug, systemSlug) {
  return `/${segment}?slug=${encodeURIComponent(slug)}&system=${encodeURIComponent(systemSlug)}`;
}

/** "a" or "an" before a word, by its first letter. */
export function articleFor(word) {
  return /^[aeiou]/i.test(String(word || '')) ? 'an' : 'a';
}
